import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { SiteSummary } from '@cms/contracts'
import { useCan } from '../auth/session.js'
import { useI18n } from '../i18n/context.js'
import { displayDomain } from '../domain/live-url.js'
import { groupSites } from '../domain/site-groups.js'
import { paths, switchSitePath } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { TextField } from '../ui/fields.js'
import { Dialog } from '../ui/overlays.js'
import { EmptyState, Icon, LinkButton, SkeletonLines, StatusChip, cx } from '../ui/primitives.js'

export function siteStatusTone(status: SiteSummary['status']): 'success' | 'warning' | 'neutral' {
  return status === 'live' ? 'success' : status === 'paused' ? 'warning' : 'neutral'
}

/**
 * The Google Cloud style project picker: the button in the top bar says which
 * client and which site you are on; the dialog lists every site you can see,
 * grouped by project, searchable by name, domain or project. Every site is a
 * link to the same section on that site, so it opens in a new tab too.
 */
export function SiteSwitcher() {
  const { t } = useI18n()
  const { sites, site, siteId, projectNames, tenant, loading } = useSiteWorkspace()
  const [open, setOpen] = useState(false)
  const location = useLocation()

  useEffect(() => setOpen(false), [location.pathname])

  const projectName = site ? projectNames.get(site.projectId) : undefined
  const clientName = tenant?.name ?? projectName

  return <>
    <button type="button" className="site-switcher" aria-haspopup="dialog" onClick={() => setOpen(true)} disabled={loading && !sites.length}>
      <span className="site-switcher-icon"><Icon name="domain" size={20}/></span>
      <span className="site-switcher-text">
        {clientName && <span className="site-switcher-client">{clientName}</span>}
        <span className="site-switcher-site">{site?.name ?? (loading ? t('common.loading') : t('switcher.noSite'))}</span>
      </span>
      {site && <span className="site-switcher-domain">{displayDomain(site.primaryDomain)}</span>}
      <Icon name="expand_more" size={20}/>
    </button>
    {open && <SiteSwitcherDialog currentSiteId={siteId} onClose={() => setOpen(false)}/>}
  </>
}

function SiteSwitcherDialog({ currentSiteId, onClose }: { currentSiteId: string; onClose: () => void }) {
  const { t } = useI18n()
  const { sites, projectNames, tenant, loading, error } = useSiteWorkspace()
  const canProvision = useCan('cms:provision')
  const location = useLocation()
  const [query, setQuery] = useState('')
  const groups = useMemo(() => groupSites(sites, projectNames, query, t('switcher.otherProject')), [sites, projectNames, query, t])

  return <Dialog
    title={tenant ? t('switcher.titleFor', { client: tenant.name }) : t('switcher.title')}
    icon="domain"
    size="md"
    onClose={onClose}
    className="switcher-dialog"
    actions={<>
      <LinkButton variant="text" icon="home" to={paths.home()} onClick={onClose}>{t('switcher.allSites')}</LinkButton>
      {canProvision && <LinkButton variant="tonal" icon="add_business" to={paths.newSite()} onClick={onClose}>{t('provision.short')}</LinkButton>}
    </>}
  >
    <TextField
      label={t('switcher.search')}
      hideLabel
      leadingIcon="search"
      type="search"
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      placeholder={t('switcher.searchPlaceholder')}
      data-autofocus
    />
    <div className="switcher-list">
      {loading && !sites.length ? <SkeletonLines count={4}/>
        : error ? <p className="field-error-text">{error}</p>
        : groups.length === 0 ? <EmptyState compact icon="search" title={query ? t('switcher.noMatch') : t('switcher.noSites')}/>
        : groups.map((group) => <section key={group.projectId} className="switcher-group" aria-label={group.projectName}>
          <h3 className="switcher-group-title"><Icon name="apartment" size={18}/>{group.projectName}</h3>
          <ul className="switcher-sites">
            {group.sites.map((entry) => <li key={entry.id}>
              <Link to={switchSitePath(location.pathname, entry.id)} className={cx('switcher-site', entry.id === currentSiteId && 'is-current')} aria-current={entry.id === currentSiteId ? 'true' : undefined} onClick={onClose}>
                <span className="switcher-site-icon"><Icon name={entry.id === currentSiteId ? 'check' : 'public'} size={20}/></span>
                <span className="switcher-site-text"><strong>{entry.name}</strong><small>{displayDomain(entry.primaryDomain)}</small></span>
                <StatusChip tone={siteStatusTone(entry.status)}>{t(`siteStatus.${entry.status}`)}</StatusChip>
              </Link>
            </li>)}
          </ul>
        </section>)}
    </div>
  </Dialog>
}
